import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Progress } from './ui/progress';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Flame, Mail } from 'lucide-react';
import { toast } from 'sonner';

import { getApiBaseUrl } from '@/lib/apiBase';
const API_URL = getApiBaseUrl();

const REFRESH_MS = 60000;
const COUNT_UP_MS = 1500;

export default function AdmissionCounter({ className = '' }) {
  const [enabled, setEnabled] = useState(null); // null = unknown yet
  const [info, setInfo] = useState(null); // { total_enrolled, limit, remaining, percentage }
  const [displayCount, setDisplayCount] = useState(0);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistJoined, setWaitlistJoined] = useState(false);

  const sectionRef = useRef(null);
  const animatedRef = useRef(false);
  const rafRef = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admissions/count`);
      setInfo(res.data);
    } catch (err) {
      // silently ignore — counter just won't render/update
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API_URL}/settings`).then((res) => {
      if (mounted) setEnabled(res.data?.show_admission_counter !== false);
    }).catch(() => {
      if (mounted) setEnabled(true);
    });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (enabled === false) return undefined;
    fetchCount();
    const interval = setInterval(fetchCount, REFRESH_MS);
    return () => clearInterval(interval);
  }, [enabled, fetchCount]);

  // Animate the count up once the section scrolls into view
  useEffect(() => {
    if (!info || animatedRef.current || !sectionRef.current) return undefined;

    const node = sectionRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !animatedRef.current) {
          animatedRef.current = true;
          const target = info.total_enrolled;
          const start = performance.now();
          const tick = (now) => {
            const progress = Math.min(1, (now - start) / COUNT_UP_MS);
            setDisplayCount(Math.round(target * (1 - Math.pow(1 - progress, 3))));
            if (progress < 1) {
              rafRef.current = requestAnimationFrame(tick);
            } else {
              setDisplayCount(target);
            }
          };
          rafRef.current = requestAnimationFrame(tick);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [info]);

  // Keep the displayed number in sync with subsequent 60s refreshes (no re-animation)
  useEffect(() => {
    if (info && animatedRef.current) setDisplayCount(info.total_enrolled);
  }, [info]);

  useEffect(() => () => rafRef.current && cancelAnimationFrame(rafRef.current), []);

  const handleJoinWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail.trim()) return;
    setWaitlistSubmitting(true);
    try {
      await axios.post(`${API_URL}/admissions/waitlist`, { email: waitlistEmail.trim() });
      setWaitlistJoined(true);
      toast.success("You're on the waitlist! We'll notify you when a seat opens up.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to join waitlist');
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  if (enabled === false || !info) return null;

  const isClosed = info.remaining <= 0;
  const isLow = info.remaining < 100 && !isClosed;

  return (
    <div ref={sectionRef} className={`max-w-2xl mx-auto mb-12 ${className}`}>
      {isClosed ? (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-2xl p-6 text-center">
          <h3 className="text-xl font-bold text-red-600 dark:text-red-400 mb-1">
            Admissions Closed — Join Waitlist
          </h3>
          <p className="text-sm text-red-500/80 dark:text-red-400/80 mb-4">
            All {info.limit} special-pricing seats have been claimed. Leave your email and we'll notify you first when more seats open.
          </p>
          {waitlistJoined ? (
            <p className="text-sm font-medium text-green-600 dark:text-green-400">You're on the list — thank you!</p>
          ) : (
            <form onSubmit={handleJoinWaitlist} className="flex gap-2 max-w-sm mx-auto">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button type="submit" disabled={waitlistSubmitting}>
                {waitlistSubmitting ? 'Joining...' : 'Join Waitlist'}
              </Button>
            </form>
          )}
        </div>
      ) : (
        <div className="bg-[#111] text-white rounded-2xl p-6 md:p-8 text-center shadow-xl">
          <p className="flex items-center justify-center gap-2 text-sm md:text-base font-bold uppercase tracking-wider text-[#E8B978] mb-4">
            <Flame className="w-4 h-4 fill-current" /> First {info.limit} Admissions — Special Pricing
          </p>
          <div className="flex items-center justify-center gap-3 mb-3">
            <span className="text-3xl md:text-4xl font-bold tabular-nums">{displayCount.toLocaleString('en-IN')}</span>
            <span className="text-gray-400 text-lg">/ {info.limit.toLocaleString('en-IN')} seats taken</span>
          </div>
          <Progress value={info.percentage} className="h-3 mb-4 bg-white/10" />
          <p className={`text-sm font-semibold ${isLow ? 'text-red-400' : 'text-gray-300'}`}>
            {isLow ? `Hurry! Only ${info.remaining} seats left` : `Only ${info.remaining} seats left at this price!`}
          </p>
        </div>
      )}
    </div>
  );
}
